import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Save, ArrowLeft, ToggleLeft, Loader2 } from 'lucide-react';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { setSelectedClient } from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import {
  API_NEWCLIENT,
  API_CLIENTS_NAME,
  API_KEY
} from '../../utils/constants';
import { authSelector } from '../../store/slices/auth';

const ReviewsSettings = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { userInfo } = useSelector(authSelector);

  // Toggle states - initialized from selectedClient
  const [automaticReviews, setAutomaticReviews] = useState(false);
  const [singlePersonBusiness, setSinglePersonBusiness] = useState(false);
  const [noReviews, setNoReviews] = useState(false);
  const [autoPublishAboveStars, setAutoPublishAboveStars] = useState('4');
  const [requireApprovalBelowStars, setRequireApprovalBelowStars] =
    useState('3');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  // Load initial values from selectedClient
  useEffect(() => {
    if (selectedClient) {
      // Artificial delay to show skeleton
      setTimeout(() => {
        setAutomaticReviews(selectedClient.automaticReviews ?? true);
        setSinglePersonBusiness(selectedClient.singlePerson ?? false);
        setNoReviews(
          selectedClient.clientFlags?.includes('NO_REVIEWS') ?? false
        );
        setAutoPublishAboveStars(selectedClient.autoPublishAboveStars ?? '4');
        setRequireApprovalBelowStars(
          selectedClient.requireApprovalBelowStars ?? '3'
        );
        setLoading(false);
      }, 600); // 600ms delay to show skeleton
    }
  }, [selectedClient]);

  const handleSaveChanges = async () => {
    if (!selectedClient) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'No client selected'
      });
      return;
    }

    setSaving(true);
    dispatch(showLoading());

    try {
      // Prepare clientFlags array
      let updatedClientFlags = [...(selectedClient.clientFlags || [])];

      if (noReviews && !updatedClientFlags.includes('NO_REVIEWS')) {
        updatedClientFlags.push('NO_REVIEWS');
      } else if (!noReviews && updatedClientFlags.includes('NO_REVIEWS')) {
        updatedClientFlags = updatedClientFlags.filter(
          (flag) => flag !== 'NO_REVIEWS'
        );
      }

      // Prepare the client data with all existing data plus the updated settings
      const clientData = {
        ...selectedClient,
        automaticReviews,
        singlePerson: singlePersonBusiness,
        clientFlags: updatedClientFlags,
        autoPublishAboveStars,
        requireApprovalBelowStars
      };

      const response = await axios({
        method: 'post',
        url: API_NEWCLIENT,
        data: clientData
      });

      if (response.status === 200) {
        // Refresh the client data
        const clData = await axios.get(
          `${API_CLIENTS_NAME}${selectedClient.clientName}`,
          {
            headers: {
              'X-API-KEY': API_KEY,
              Authorization: `bearer ${userInfo.accessToken}`
            }
          }
        );

        if (clData.data) {
          dispatch(setSelectedClient(clData.data));
        }

        setSuccessModalOpen(true);
      }
    } catch (error) {
      console.error('Error saving review settings:', error);
      setErrorModalOpen(true);
    } finally {
      setSaving(false);
      dispatch(hideLoading());
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 overflow-x-hidden">
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <main className="p-3 sm:p-6 lg:p-10 space-y-6 sm:space-y-8">
          {/* Page Title */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 sm:gap-4 mb-2">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl shrink-0"
                onClick={() => history.push('/reviews')}>
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                  Review Settings,{' '}
                  {selectedClient ? `${selectedClient.clientName}` : ''}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Enable or disable automated review response features
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Button
                size="lg"
                onClick={handleSaveChanges}
                disabled={saving}
                style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 sm:h-14 w-full sm:w-auto text-sm sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-2 sm:gap-3 flex items-center justify-center">
                <Save className="h-4 w-4 sm:h-5 sm:w-5" />
                {saving && (
                  <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                )}
                {!saving && (
                  <span className="hidden sm:inline">Save Changes</span>
                )}
                {!saving && <span className="sm:hidden">Save</span>}
              </Button>
            </div>
          </div>

          {/* Google Preferences Card */}
          {loading ? (
            <>
              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gray-200 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-6 w-40 sm:w-48 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-full max-w-64 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {/* Skeleton Settings Items */}
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start justify-between gap-3 py-4 ${
                        idx < 2 ? 'border-b border-border' : ''
                      }`}>
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
                        <div className="h-4 w-full max-w-56 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse shrink-0 mt-1" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gray-200 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="h-6 w-40 sm:w-48 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-full max-w-64 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <div className="space-y-2 min-w-0">
                    <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-full max-w-72 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from({ length: 2 }).map((_, idx) => (
                      <div
                        key={idx}
                        className="h-40 bg-gray-200 rounded-xl animate-pulse"
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Google Preferences Card */}
              <Card className="shadow-md rounded-xl sm:rounded-2xl border-0">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <ToggleLeft className="h-5 w-5 scale-x-125 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold break-words">
                        Manage Preferences Google
                      </CardTitle>
                      <CardDescription className="text-sm sm:text-base mt-1">
                        Enable or disable automated review response features
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {/* Automatic Reviews */}
                  <div className="flex items-start justify-between gap-3 py-4 border-b border-border">
                    <div className="space-y-1 min-w-0 pr-2">
                      <Label className="text-sm sm:text-base font-semibold leading-snug block">
                        Auto-Respond to Reviews
                      </Label>
                      <p className="text-sm text-muted-foreground leading-snug">
                        Automatically respond to customer reviews
                      </p>
                    </div>
                    <Switch
                      checked={automaticReviews}
                      onCheckedChange={setAutomaticReviews}
                      className="data-[state=checked]:bg-primary scale-75 shrink-0 mt-1"
                    />
                  </div>

                  {/* Single Person Business */}
                  <div className="flex items-start justify-between gap-3 py-4 border-b border-border">
                    <div className="space-y-1 min-w-0 pr-2">
                      <Label className="text-sm sm:text-base font-semibold leading-snug block">
                        Solo Operator / Single-Person Business
                      </Label>
                      <p className="text-sm text-muted-foreground leading-snug">
                        This business is operated by a single person
                      </p>
                    </div>
                    <Switch
                      checked={singlePersonBusiness}
                      onCheckedChange={setSinglePersonBusiness}
                      className="data-[state=checked]:bg-primary scale-75 shrink-0 mt-1"
                    />
                  </div>

                  {/* No Reviews */}
                  <div className="flex items-start justify-between gap-3 py-4">
                    <div className="space-y-1 min-w-0 pr-2">
                      <Label className="text-sm sm:text-base font-semibold leading-snug block">
                        Disable Review Management
                      </Label>
                      <p className="text-sm text-muted-foreground leading-snug">
                        Disable review management features
                      </p>
                    </div>
                    <Switch
                      checked={noReviews}
                      onCheckedChange={setNoReviews}
                      className="data-[state=checked]:bg-primary scale-75 shrink-0 mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </div>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Review settings updated successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setSuccessModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to update review settings.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReviewsSettings;
